import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import AchClient, {AchItem} from '../client/ach/AchClient';
import {query} from '@trlogic/client-js';
import {TransactionData, TransactionReason} from '../domain/stream/transaction/TransactionDomains';
import {User} from 'domain/rbac/RbacDomains';
import {BatchCloseCaseJob, CaseSettings, CloseCaseBulkActionOperation} from 'domain/case/CaseDomains';
import {BULK_CLOSE_NOT_PERMITTED_ERROR, canPerformBulkClose,} from '../util/CaseBulkCloseUtil';
import {CustomFilter} from 'domain/ui/FilterDomains';
import {store} from 'redux/store';
import FilterUtil from 'util/FilterUtil';
import JobClient from 'client/job/JobClient';
import {TransactionDataExportJob} from '../domain/job/JobDomain';
import ServiceContainer from '../config/ServiceContainer';
import {ServiceNames} from '../constant/ServiceNames';
import JobService from './JobService';
import SelectQuery = query.SelectQuery;
import QueryBuilder = query.QueryBuilder;
import Filter = query.Filter;
import Order = query.Order;
import empty = query.empty;
import and = query.and;
import eq = query.eq;

class AchService {
  private achClient: AchClient = GatewayContainer.getInstance().get(GatewayNames.ACH_CLIENT);
  private jobClient: JobClient = GatewayContainer.getInstance().get(GatewayNames.JOB_CLIENT);
  private _jobService: JobService | undefined;

  private get jobService(): JobService {
    if (this._jobService === undefined) {
      this._jobService = ServiceContainer.getInstance().get(ServiceNames.JOB);
    }
    return this._jobService!;
  }

  public uploadAchFile(file: File): Promise<string> {
    return this.achClient.uploadAchFile(file);
  }

  public getAchFiles(query: SelectQuery): Promise<AchItem[]> {
    return this.achClient.getAchFiles(query);
  }

  public getAchFileCount(query: SelectQuery): Promise<number> {
    return this.achClient.getAchFileCount(query);
  }

  public getScores(domain: string, options?: GetAchOptions): Promise<Record<string, number>> {
    return this.achClient.getScores(domain, options);
  }

  public getEnrichmentVariable(enrichments: string[], transactionData: TransactionData): Promise<any> {
    return this.achClient.getEnrichmentVariable(enrichments, transactionData);
  }

  public getTransactionsByFileName(fileName: string, query: SelectQuery): Promise<{ data: TransactionData[]; count: number }> {
    return this.achClient.getTransactionsByFileName(fileName, query);
  }

  public buildTransactionQuery(fileName: string, currentPage: number, rows: number, sort: Order[], filter?: Filter): SelectQuery {
    if (!fileName) {
      return QueryBuilder.select().where(empty()).build();
    }

    const defaultSort: Order[] = sort.length > 0 ? sort : [{ field: 'createdAt', operator: 'DESC' as const }];

    const queryBuilder = QueryBuilder.select()
      .skip((currentPage - 1) * rows)
      .limit(rows)
      .orderBy(...defaultSort);

    if (filter) {
      queryBuilder.where(and(eq('variables.fileName.keyword', fileName), filter));
    } else {
      queryBuilder.where(eq('variables.fileName.keyword', fileName));
    }

    return queryBuilder.build();
  }

  public buildCombinedFilter(fileName: string, userFilter?: Filter): Filter {
    const fileNameFilter = eq('variables.fileName.keyword', fileName);
    return userFilter ? and(fileNameFilter, userFilter) : fileNameFilter;
  }

  public async closeBulkCase(
    user: User,
    domain: string,
    ids: CloseCaseBulkActionOperation[],
    type: TransactionReason.FRAUD | TransactionReason.NOT_FRAUD | TransactionReason.IN_COMPLIANT | TransactionReason.COMPLIANT,
    reason?: string,
    caseSettings?: CaseSettings,
  ): Promise<void> {
    if (!canPerformBulkClose(user.username, caseSettings, 'ACH')) {
      throw new Error(BULK_CLOSE_NOT_PERMITTED_ERROR);
    }
    if (ids.length == 0) {
      try {
        const filter: CustomFilter = store.getState().page.filter.filter;
        const parsedFilter = FilterUtil.parse(filter);
        const job: BatchCloseCaseJob = {
          domain: domain,
          closeCode: reason,
          type: 'CASE_BATCH_CLOSE',
          caseStatus: type,
          username: user.username,
          status: 'PENDING',
          timestamp: Date.now(),
          filter: parsedFilter,
        };
        await this.jobClient.schedule(job);
      } catch (e) {
        console.error(e);
      }
    } else {
      return this.achClient.closeBulk(domain, ids, type, reason);
    }
  }

  public closeCase(domain: string, index: string, type: string, reason?: string): Promise<void> {
    return this.achClient.closeCase(domain, index, type, reason);
  }

  public holdCase(domain: string, index: string, reason?: string): Promise<void> {
    return this.achClient.holdCase(domain, index, reason);
  }

  public assign(domain: string, index: string, assign: string | null) {
    return this.achClient.assign(domain, index, assign);
  }

  public async exportTransactions(job: TransactionDataExportJob): Promise<void> {
    await this.jobService.schedule(job);
  }

  public async getTransactionByFileNameAndId(fileName: string, transactionId: string): Promise<TransactionData | undefined> {
    let decodedId: string | null = null;
    try {
      decodedId = atob(transactionId);
    } catch (e) {}

    let result: { data: TransactionData[]; count: number } | undefined = undefined;

    if (decodedId) {
      const decodedQuery = QueryBuilder.select().where(eq('id.keyword', decodedId)).limit(1).build();
      result = await this.getTransactionsByFileName(fileName, decodedQuery);
    }

    if (!result || !result.data || result.data.length === 0) {
      const selectQuery = QueryBuilder.select().where(eq('id.keyword', transactionId)).limit(1).build();
      result = await this.getTransactionsByFileName(fileName, selectQuery);
    }

    let transaction: TransactionData | undefined = undefined;

    const normalizeBase64 = (str: string): string => {
      return str.replace(/=+$/, '');
    };

    if (result && result.data && result.data.length > 0) {
      transaction = result.data.find((t: TransactionData) => {
        return normalizeBase64(t.id) === normalizeBase64(transactionId);
      });

      if (!transaction) {
        transaction = result.data.find((t: TransactionData) => t.id === transactionId);
      }

      if (!transaction && decodedId) {
        transaction = result.data.find((t: TransactionData) => t.id === decodedId);
      }

      if (!transaction && decodedId) {
        transaction = result.data.find((t: TransactionData) => {
          try {
            const decodedTId = atob(t.id);
            return decodedTId === decodedId;
          } catch (e) {
            return false;
          }
        });
      }
    }

    if (!transaction) {
      const fallbackQuery = QueryBuilder.select().limit(1000).build();
      const fallbackResult = await this.getTransactionsByFileName(fileName, fallbackQuery);

      if (fallbackResult.data && fallbackResult.data.length > 0) {
        transaction = fallbackResult.data.find((t: TransactionData) => {
          return normalizeBase64(t.id) === normalizeBase64(transactionId);
        });
        if (!transaction) {
          transaction = fallbackResult.data.find((t: TransactionData) => t.id === transactionId);
        }
        if (!transaction && decodedId) {
          transaction = fallbackResult.data.find((t: TransactionData) => t.id === decodedId);
        }

        if (!transaction && decodedId) {
          transaction = fallbackResult.data.find((t: TransactionData) => {
            try {
              const decodedTId = atob(t.id);
              return decodedTId === decodedId;
            } catch (e) {
              return false;
            }
          });
        }
      }
    }

    return transaction;
  }
}

export default AchService;

export type GetAchOptions = {
  key?: string;
  keyValue?: string;
  skip?: number;
  limit?: number;
};
